import { type SelectionDelta } from '../../machine/deltas/types.ts';
import { type SelectionState } from '../../machine/state/types.ts';
import { type SelectionEffect } from '../types.ts';

type NonNoopDeltaKind = Exclude<SelectionDelta['kind'], 'noop'>;

type EffectConfig<T extends readonly NonNoopDeltaKind[]> = {
  deltaTypes: T;
  onDelta(
    delta: Extract<SelectionDelta, { kind: T[number] }>,
    state: SelectionState
  ): void;
  setup?(): void;
  dispose?(): void;
};

/**
 * Creates an effect factory with full type inference and dependency injection.
 *
 * The const generic preserves literal `deltaTypes` without callers adding
 * `as const`; the returned effect stores those kinds in a Set for O(1)
 * filtering.
 *
 * The factory signature matches the builder's parameters, enabling type-safe
 * dependency injection. Effects with no dependencies can be passed directly
 * to EffectManager; effects with dependencies must be curried.
 *
 * @example
 * ```typescript
 * // No dependencies - passes directly to EffectManager
 * export const createHoverDeleteEffect = createEffect(() => ({
 *   deltaTypes: ['hoverToDeleteChanged'],
 *   onDelta(delta) {
 *     document.body.classList.toggle('hover-to-delete', delta.enabled);
 *   },
 * }));
 *
 * // With dependencies - must be curried when passed to EffectManager
 * export const createAnalyticsEffect = createEffect((analytics: Analytics) => ({
 *   deltaTypes: ['selectionModified'],
 *   onDelta(delta) {
 *     analytics.track('selection', { count: delta.addedCells.length });
 *   },
 * }));
 *
 * // Usage in EffectManager:
 * new EffectManager(machine, [
 *   createHoverDeleteEffect,                    // ✓ no deps
 *   () => createAnalyticsEffect(analyticsInstance), // ✓ curried
 *   createAnalyticsEffect,                      // ✗ TypeScript error!
 * ]);
 * ```
 */
export function createEffect<
  Args extends unknown[],
  const T extends readonly NonNoopDeltaKind[]
>(builder: (...args: Args) => EffectConfig<T>): (...args: Args) => SelectionEffect {
  return (...args: Args) => {
    // This will not be a large array
    const config = builder(...args);

    return {
      onDelta: config.onDelta,
      setup: config.setup,
      dispose: config.dispose,
      deltaTypes: new Set(config.deltaTypes)
    };
  };
}
